/**
 * Links into this app that belong to Manage (mission-control-web), the admin console.
 *
 * The catering approval email (rdx-platform-api, `notifications/templates/email/mjml/seeds/catering.ts`) linked
 * its "Review order" button to `/catering/approvals` here. This app has no approvals page, so every approver
 * landed on Home. Nath, 5 October 2026: "For catering approvers it should go to the manage application." The API
 * now links the email to Manage's Requests board directly, which opens on "Needs approval" for anyone holding
 * `fulfillment:manage`. Emails already sent still carry the old link, so `/catering/approvals` here sends the
 * browser on to the same board, with its query.
 *
 * Manage's origin is `environment.manageUrl` (`NG_APP_MANAGE_URL`, or `NG_APP_MISSION_CONTROL_URL` from the deploy
 * workflows), the same value the asset page's "Manage" link uses. Manage routes paths, not fragments, and enforces
 * its own sign-in, so this route has no `authGuard`: an approver is not made to sign in here first.
 *
 * Framework-light, like `retired-home-routes.ts`: it imports no component, so `app.routes.ts` can use it without
 * growing the initial bundle.
 */
import { DOCUMENT } from '@angular/common';
import { InjectionToken, inject } from '@angular/core';
import { Router, type CanActivateFn, type Params } from '@angular/router';

import { environment } from '../../../environments/environment';

/** Manage's Requests board (mission-control-web `src/app/app.routes.ts` → `requests`). */
export const MANAGE_CATERING_APPROVALS_PATH = '/requests';

/**
 * Leaves this app for another app's URL, replacing the current history entry so Back does not bounce through the
 * redirect. A token so a spec can watch the hand-off instead of navigating jsdom.
 */
export const OPEN_OTHER_APP = new InjectionToken<(url: string) => void>('OPEN_OTHER_APP', {
  providedIn: 'root',
  factory: () => {
    const doc = inject(DOCUMENT);
    return (url: string) => doc.location.replace(url);
  },
});

/**
 * An absolute URL into Manage, or null when its configured origin is not an http(s) URL. A bad value must never
 * become a `javascript:` or `data:` navigation.
 */
export function manageUrl(path: string, queryParams: Params = {}, base: string = environment.manageUrl ?? ''): string | null {
  let origin: string;
  try {
    const parsed = new URL(base);
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') return null;
    origin = parsed.origin;
  } catch {
    return null;
  }
  const url = new URL(path, origin);
  for (const [key, value] of Object.entries(queryParams)) {
    for (const item of Array.isArray(value) ? value : [value]) {
      if (item != null) url.searchParams.append(key, String(item));
    }
  }
  return url.toString();
}

/**
 * `/catering/approvals`: Manage's Requests board, with the query carried across. When Manage's origin is not usable
 * the approver lands on Home, where the wildcard sent them before.
 */
export const toManageCateringApprovals: CanActivateFn = (route) => {
  const href = manageUrl(MANAGE_CATERING_APPROVALS_PATH, route.queryParams);
  if (!href) return inject(Router).createUrlTree(['/home']);
  inject(OPEN_OTHER_APP)(href);
  return false;
};
